import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { describeError } from '@/shared/hooks/useAsync';
import { useSession } from '@/shared/session/useSession';
import { Banner, Button, Field, PasswordField } from '@/shared/ui';

/** RN-17 · el único requisito es el largo. Ninguna exigencia de composición. */
const MINIMUM_LENGTH = 12;

export function RegisterPage() {
  const { status, register } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [attempted, setAttempted] = useState(false);

  if (status === 'authenticated') {
    return <Navigate to="/perfil" replace />;
  }

  const missing = Math.max(0, MINIMUM_LENGTH - password.length);
  const tooShort = missing > 0;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);

    /*
     * La validación corre al enviar y el botón queda habilitado.
     *
     * Antes el botón se deshabilitaba mientras faltaran caracteres, y un botón muerto no explica
     * nada: quien no lee la línea chica se queda tocando algo que no responde. Habilitado, el
     * intento produce una respuesta.
     */
    if (tooShort) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Igual que al ingresar: el espacio que agrega el autocompletado del teclado móvil llegaría
      // a la base como parte de la dirección.
      await register(email.trim(), password);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="auth-screen" onSubmit={onSubmit} noValidate>
      <div className="stack">
        <h1>Crear una cuenta</h1>
        <p className="muted">Después te vamos a pedir tres datos para poder calcular tu gasto.</p>
      </div>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <div className="stack">
        <Field
          label="Correo electrónico"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          required
          value={email}
          onChange={(event) => {
            setError(null);
            setEmail(event.target.value);
          }}
        />

        <PasswordField
          label="Contraseña"
          autoComplete="new-password"
          required
          value={password}
          onChange={(event) => {
            setError(null);
            setPassword(event.target.value);
          }}
          // Mientras se escribe, la ayuda cuenta cuánto falta en gris. Sólo pasa a ser un error
          // después de intentar enviar: teñir de rojo una contraseña a medio tipear es acusar de
          // un error que todavía no ocurrió.
          hint={
            tooShort && password.length > 0
              ? `Faltan ${missing} caracteres.`
              : `Al menos ${MINIMUM_LENGTH} caracteres. Podés usar espacios y una frase entera.`
          }
          error={attempted && tooShort ? `Faltan ${missing} caracteres para el mínimo.` : undefined}
        />
      </div>

      <Button type="submit" variant="primary" block disabled={submitting}>
        {submitting ? 'Creando…' : 'Crear cuenta'}
      </Button>

      <p className="small muted" style={{ textAlign: 'center' }}>
        ¿Ya tenés una? <Link to="/ingresar">Entrar</Link>
      </p>
    </form>
  );
}
